import React, { useState, useEffect } from "react";
import { Provider } from "react-redux";
import { BrowserRouter as Router } from "react-router-dom";
import { datadogRum } from "@datadog/browser-rum";
import { ConfigProvider } from "antd";
import enUS from "antd/es/locale/en_US";
import esES from "antd/es/locale/es_ES";
import ptBR from "antd/es/locale/pt_BR";
import AppRoutes from "components/routes/routes";
import dayjs from "dayjs";
import Store from "store/store-config";
import { ThemeProvider } from "styled-components";
import GlobalStyle from "styles/global";
import theme from "styles/theme";
import { readConfigFile } from "utils/appConfig";
import { AuthProvider } from "utils/auth/authContext";
import { languageOptions } from "utils/functions";
import getLocale, { getTimeZoneName } from "utils/intl/getLocale";
import getMessages from "utils/intl/getMessages";
import IntlProvider, { IFormatsType } from "utils/intl/IntlProvider";
import "dayjs/locale/pt-br";
import "dayjs/locale/es";
import "dayjs/locale/en";
import "antd/dist/antd.css";
import "styles/celebration.css";

const YmsApp = () => {
  const [localeState, setLocaleState] = useState(ptBR);
  const [messages, setMessages] = useState({});
  const [locale, setLocale] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const defaultLocale = languageOptions[0].description;

  useEffect(() => {
    const fetchLocale = async () => {
      try {
        const fetchedLocale = await getLocale();
        setLocale(fetchedLocale);
      } catch (error) {
        setLocale(defaultLocale);
      }
    };

    fetchLocale();
  }, []);

  useEffect(() => {
    const translateLocale = () => {
      switch (locale) {
        case "en-US":
          setLocaleState(enUS);
          dayjs.locale("en");
          break;
        case "es-MX":
          setLocaleState(esES);
          dayjs.locale("es");
          break;
        default:
          setLocaleState(ptBR);
          dayjs.locale("pt-br");
          break;
      }
    };

    if (locale) {
      translateLocale();
    }
  }, [locale]);

  useEffect(() => {
    const loadMessages = async () => {
      if (!localeState.locale) return;

      try {
        const messages = await getMessages(localeState.locale);
        setMessages(messages);
      } catch (error) {
        console.error("Erro ao carregar mensagens:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadMessages();
  }, [localeState]);

  useEffect(() => {
    const initializeDatadog = async () => {
      try {
        const config = await readConfigFile();
        if (config.VITE_YMS_API_MOCK === "false") {
          datadogRum.init({
            applicationId: `${config.VITE_DD_APPLICATION_ID}`,
            clientToken: `${config.VITE_DD_CLIENT_TOKEN_ID}`,
            site: "datadoghq.com",
            service: `${config.VITE_DD_SERVICE}`,
            env: `${config.VITE_DD_RUM_ENV}`,
            sampleRate: 100,
            trackInteractions: true,
            silentMultipleInit: true,
            allowedTracingOrigins: [`${config.VITE_YMS_WEBSITE}`],

            beforeSend: (event) => {
              const message = (event.error as { message?: string })?.message;
              if (message?.includes("ResizeObserver loop limit exceeded") || message?.includes("ResizeObserver loop completed with undelivered notifications")) {
                return false;
              }
            },
          });
        }
      } catch (error) {
        console.error("Erro ao obter configuração ou inicializar Datadog:", error);
      }
    };

    initializeDatadog();
  }, []);

  const formats: IFormatsType = { time: { "24hour": { hour12: false, hours: "numeric", minute: "numeric" } } };
  const timeZone = getTimeZoneName();

  if (isLoading) {
    return null;
  }

  return (
    <Router>
      <AuthProvider>
        <ThemeProvider theme={theme}>
          <IntlProvider locale={locale} messages={messages} formats={formats} timeZone={timeZone} defaultLocale={defaultLocale}>
            <Provider store={Store}>
              <ConfigProvider locale={localeState}>
                <AppRoutes />
              </ConfigProvider>
            </Provider>
          </IntlProvider>
          <GlobalStyle />
        </ThemeProvider>
      </AuthProvider>
    </Router>
  );
};

export default YmsApp;
